/**
 * SplitRuleEditor — Story 2.3-UI (Split-Editor, Referenz splits.html)
 *
 * Editor-Formular fuer host_split_rules (POST /api/v2/host_split_rules bzw.
 * PATCH /api/v2/host_split_rules/:id) mit debouncter Live-Vorschau ueber
 * POST /api/v2/host_split_rules/preview:
 *   „Bei 100 GB × 0,11 $/GB erhält Partner X: Y HNT“
 *
 * - 422 beim Speichern (z.B. Summe ueberlappender %-Splits > 100 %) zeigt die
 *   lokalisierte BE-Meldung als Alert an.
 * - 422 der Vorschau (ZeroRate / fehlender Wechselkurs) wird in der
 *   Vorschau-Zeile abgefangen statt das Formular zu blockieren.
 * - Annahmen (GB, $/GB, HNT-Ertrag, Kurse) sind editierbare Formularwerte mit
 *   den Defaults der Vorlage (100 GB, 0,11 $/GB); Kurse sind bewusst leer und
 *   werden nur mitgesendet, wenn eingegeben (keine erfundenen Kurse).
 */
import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '../../components/ui'
import { toast } from '../../components/Toast/useToast'
import { apiErrorMessage, parseDecimal, validateSplitRuleForm } from './splitRuleHelpers'
import { useSplitPreview, type SplitPreviewRequest } from './useSplitPreview'
import {
  useCreateSplitRule,
  useHosts,
  useHotspotOptions,
  useSplitRule,
  useUpdateSplitRule,
  type SplitRulePayload,
} from './useSplitRules'

type SplitType = 'percentage' | 'fixed_amount'
type Basis = 'hnt_rewards' | 'gigabytes_transferred'
type Currency = 'HNT' | 'EUR' | 'USD'

const CURRENCIES: Currency[] = ['HNT', 'EUR', 'USD']

type FormState = {
  host_id: string
  // P1-8: Mehrfachauswahl (Vorlage splits.html erlaubt eine Regel auf
  // mehrere Hotspots anzuwenden). Backend kennt nur hotspot_id pro Regel —
  // beim Speichern wird pro ausgewaehltem Hotspot ein eigener POST geschickt.
  hotspot_ids: string[]
  split_type: SplitType
  basis: Basis
  payout_currency: Currency
  value: string
  start_date: string
  end_date: string
  notes: string
}

type Assumptions = {
  gigabytes: string
  rate_per_gb: string
  hnt_rewards: string
  hnt_usd: string
  eur_usd: string
}

const EMPTY_FORM: FormState = {
  host_id: '',
  hotspot_ids: [],
  split_type: 'percentage',
  basis: 'hnt_rewards',
  payout_currency: 'HNT',
  value: '30',
  start_date: '',
  end_date: '',
  notes: '',
}

// Defaults der Vorlage splits.html: „Bei 100 GB × 0,11 $/GB“.
const DEFAULT_ASSUMPTIONS: Assumptions = {
  gigabytes: '100',
  rate_per_gb: '0.11',
  hnt_rewards: '10',
  hnt_usd: '',
  eur_usd: '',
}

export type SplitRuleEditorProps =
  | {
      mode: 'create'
      id?: undefined
      /**
       * Story frontend-host-form (#4): vorbelegter Host, wenn der Editor aus
       * der Host-Detailansicht heraus geoeffnet wird (Deep-Link
       * /splits/create?host_id=…). Rein clientseitige Vorbelegung — der
       * Host bleibt im Select aenderbar.
       */
      defaultHostId?: number
    }
  | { mode: 'edit'; id: number }

export function SplitRuleEditor(props: SplitRuleEditorProps) {
  const { t } = useTranslation('splits')
  const navigate = useNavigate()

  const hostsQuery = useHosts()
  const hotspotsQuery = useHotspotOptions()
  const ruleQuery = useSplitRule(props.mode === 'edit' ? props.id : null)
  const rule = props.mode === 'edit' ? ruleQuery.data : undefined

  // Issue #52: der bereits zugeordnete Hotspot einer Regel muss im Editor
  // erscheinen, auch wenn er (z.B. inaktiv/HIP-134-Quote) nicht (mehr) in
  // den `owned_only`-Optionen auftaucht — sonst zeigt die Checkbox-Liste
  // im Edit-Modus den echten Hotspot der Regel weder an noch angehakt.
  const hotspotOptions = useMemo(() => {
    const options = hotspotsQuery.data ?? []
    if (rule?.hotspot_id == null || options.some((h) => h.id === rule.hotspot_id)) {
      return options
    }
    return [...options, { id: rule.hotspot_id, name: rule.hotspot_name ?? String(rule.hotspot_id) }]
  }, [hotspotsQuery.data, rule])

  // Edits liegen als Overlay ueber dem Initialstand (EMPTY_FORM bzw. der
  // geladenen Regel) — kein setState-in-Effect, die Route remountet den
  // Editor per key={id} bei Regelwechsel.
  const [edits, setEdits] = useState<Partial<FormState>>({})
  const [assumptions, setAssumptions] = useState<Assumptions>(DEFAULT_ASSUMPTIONS)

  const initialForm = useMemo<FormState>(() => {
    if (!rule) {
      return props.mode === 'create' && props.defaultHostId !== undefined
        ? { ...EMPTY_FORM, host_id: String(props.defaultHostId) }
        : EMPTY_FORM
    }
    return {
      host_id: rule.host_id != null ? String(rule.host_id) : '',
      hotspot_ids: rule.hotspot_id != null ? [String(rule.hotspot_id)] : [],
      split_type: rule.split_type ?? 'percentage',
      basis: rule.basis ?? 'hnt_rewards',
      payout_currency: rule.payout_currency ?? 'HNT',
      value:
        rule.split_type === 'fixed_amount'
          ? String(rule.fixed_amount ?? '')
          : String(rule.percentage ?? ''),
      start_date: rule.start_date ?? '',
      end_date: rule.end_date ?? '',
      notes: rule.notes ?? '',
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule, props.mode === 'create' ? props.defaultHostId : undefined])
  const form: FormState = { ...initialForm, ...edits }

  const createMutation = useCreateSplitRule()
  const updateMutation = useUpdateSplitRule()
  const saveMutation = props.mode === 'create' ? createMutation : updateMutation

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setEdits((e) => ({ ...e, [key]: value }))
  const setAssumption = <K extends keyof Assumptions>(key: K, value: string) =>
    setAssumptions((a) => ({ ...a, [key]: value }))
  const toggleHotspot = (id: string) =>
    set(
      'hotspot_ids',
      form.hotspot_ids.includes(id)
        ? form.hotspot_ids.filter((h) => h !== id)
        : [...form.hotspot_ids, id],
    )

  const valueNum = parseDecimal(form.value)

  // --- Clientseitige Validierung (F7) --------------------------------------
  // Vorfilter fuer offensichtliche Eingabefehler; das 422 des Backends bleibt
  // der Fallback fuer alles nur serverseitig Entscheidbare (Summe > 100 % ...).
  const fieldErrors = validateSplitRuleForm({
    split_type: form.split_type,
    value: form.value,
    start_date: form.start_date,
    end_date: form.end_date,
  })
  const hasFieldErrors = Object.keys(fieldErrors).length > 0
  const valueError = fieldErrors.value ? t(fieldErrors.value) : null
  const endDateError = fieldErrors.end_date ? t(fieldErrors.end_date) : null

  // --- Live-Vorschau-Request (debounced im Hook) ---------------------------
  const hasValueError = fieldErrors.value != null
  const previewRequest = useMemo<SplitPreviewRequest | null>(() => {
    if (valueNum == null || hasValueError) return null

    const base: SplitPreviewRequest = {
      split_type: form.split_type,
      basis: form.basis,
      payout_currency: form.payout_currency,
    }
    if (form.split_type === 'percentage') base.percentage = valueNum
    else base.fixed_amount = valueNum

    if (form.basis === 'gigabytes_transferred') {
      const gb = parseDecimal(assumptions.gigabytes)
      const rate = parseDecimal(assumptions.rate_per_gb)
      if (gb == null || rate == null) return null
      base.gigabytes = gb
      // rate == 0 wird bewusst mitgesendet: das Backend antwortet mit einem
      // 422 (ZeroRate), dessen Meldung in der Vorschau-Zeile erscheint.
      base.rate_per_gb = rate
    } else {
      const hnt = parseDecimal(assumptions.hnt_rewards)
      if (hnt == null) return null
      base.hnt_rewards = hnt
    }

    const hntUsd = parseDecimal(assumptions.hnt_usd)
    const eurUsd = parseDecimal(assumptions.eur_usd)
    if (hntUsd != null || eurUsd != null) {
      base.exchange_rates = {}
      if (hntUsd != null) base.exchange_rates.hnt_usd = hntUsd
      if (eurUsd != null) base.exchange_rates.eur_usd = eurUsd
    }
    return base
  }, [form.split_type, form.basis, form.payout_currency, valueNum, hasValueError, assumptions])

  const preview = useSplitPreview(previewRequest)

  // Braucht die %-Umrechnung Kurse? (Basis GB -> USD, Basis HNT -> HNT)
  const revenueCurrency: Currency = form.basis === 'gigabytes_transferred' ? 'USD' : 'HNT'
  const needsConversion =
    form.split_type === 'percentage' && form.payout_currency !== revenueCurrency
  const needsHntUsd =
    needsConversion && (revenueCurrency === 'HNT' || form.payout_currency === 'HNT')
  const needsEurUsd = needsConversion && form.payout_currency === 'EUR'

  // --- Speichern -----------------------------------------------------------
  const hostId = parseDecimal(form.host_id)
  const hotspotIds = form.hotspot_ids
    .map((id) => parseDecimal(id))
    .filter((id): id is number => id != null)
  const canSave =
    hostId != null && hotspotIds.length > 0 && valueNum != null && !hasFieldErrors
  const [bulkError, setBulkError] = useState<string | null>(null)

  const buildPayload = (hotspotId: number): SplitRulePayload => ({
    host_id: hostId as number,
    hotspot_id: hotspotId,
    split_type: form.split_type,
    basis: form.basis,
    payout_currency: form.payout_currency,
    percentage: form.split_type === 'percentage' ? valueNum : null,
    fixed_amount: form.split_type === 'fixed_amount' ? valueNum : null,
    start_date: form.start_date || null,
    end_date: form.end_date || null,
    notes: form.notes || null,
  })

  const handleSave = async () => {
    if (!canSave) return
    setBulkError(null)

    if (props.mode === 'edit') {
      updateMutation.mutate(
        { id: props.id, payload: buildPayload(hotspotIds[0]) },
        { onSuccess: () => void navigate({ to: '/splits' }) },
      )
      return
    }

    // P1-8: eine Regel auf mehrere Hotspots anwenden -> N parallele POSTs
    // (das Backend-Modell kennt nur hotspot_id pro Regel). Sammel-Fehler-
    // behandlung: Erfolge zaehlen fuer den Toast, Fehlschlaege bleiben als
    // Alert stehen, das Formular wird nur bei vollstaendigem Erfolg verlassen.
    const results = await Promise.allSettled(
      hotspotIds.map((id) => createMutation.mutateAsync(buildPayload(id))),
    )
    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    const failed = results.filter((r) => r.status === 'rejected')

    if (succeeded > 0) {
      toast.success(t('editor.saveBulkSuccess', { count: succeeded }))
    }
    if (failed.length > 0) {
      const message = apiErrorMessage((failed[0] as PromiseRejectedResult).reason)
      setBulkError(
        t('editor.saveBulkPartialFailure', {
          count: failed.length,
          message: message ?? t('editor.saveFailed'),
        }),
      )
      return
    }
    void navigate({ to: '/splits' })
  }

  // --- Editier-Modus: Lade-/Fehlerzustand ----------------------------------
  if (props.mode === 'edit' && ruleQuery.isLoading) {
    return <div className="card skeleton" style={{ height: 420 }} data-testid="splits-editor-loading" />
  }
  if (props.mode === 'edit' && ruleQuery.isError) {
    return (
      <div className="card card-pad" data-testid="splits-editor-load-error">
        <p className="alert-error" role="alert">
          {t('editor.loadError')}
        </p>
      </div>
    )
  }

  const hostName =
    hostsQuery.data?.find((h) => String(h.id) === form.host_id)?.name ??
    t('preview.partnerFallback')

  const saveError =
    bulkError ??
    (saveMutation.isError && props.mode === 'edit'
      ? apiErrorMessage(saveMutation.error) ?? t('editor.saveFailed')
      : null)

  const valueLabel =
    form.split_type === 'percentage'
      ? t('editor.valuePercent')
      : t('editor.valueFixed', { currency: form.payout_currency })

  return (
    <div className="card card-pad" data-testid="splits-editor">
      <h3 style={{ marginBottom: 4 }}>{t('editor.title')}</h3>
      <p className="muted" style={{ fontSize: 12, margin: '0 0 16px' }} data-testid="splits-editor-subtitle">
        {props.mode === 'create'
          ? t('editor.subtitleCreate')
          : t('editor.subtitleEdit', { host: hostName })}
      </p>

      <div className="col" style={{ gap: 14 }}>
        {/* Host */}
        <div>
          <span className="label" id="splits-host-label">{t('editor.host')}</span>
          <select
            className="select"
            aria-labelledby="splits-host-label"
            value={form.host_id}
            onChange={(e) => set('host_id', e.target.value)}
            data-testid="splits-host-select"
          >
            <option value="">{t('editor.selectPlaceholder')}</option>
            {(hostsQuery.data ?? []).map((h) => (
              <option key={h.id} value={String(h.id)}>
                {h.name}
              </option>
            ))}
          </select>
        </div>

        {/* Hotspots — Mehrfachauswahl (P1-8): eine Regel auf mehrere
            Hotspots anwenden. Im Edit-Modus bleibt es faktisch eine
            Auswahl, da die geladene Regel bereits an genau einen Hotspot
            gebunden ist. */}
        <div>
          <span className="label" id="splits-hotspot-label">
            {t('editor.hotspot')}
            {form.hotspot_ids.length > 1 && (
              <span className="muted" style={{ fontWeight: 400 }}>
                {' '}
                {t('editor.hotspotSelectedCount', { count: form.hotspot_ids.length })}
              </span>
            )}
          </span>
          <div
            className="col"
            role="group"
            aria-labelledby="splits-hotspot-label"
            style={{ gap: 6, maxHeight: 180, overflowY: 'auto' }}
            data-testid="splits-hotspot-list"
          >
            {hotspotOptions.map((h) => (
              <label key={h.id} className="row" style={{ gap: 8, alignItems: 'center', fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={form.hotspot_ids.includes(String(h.id))}
                  onChange={() => toggleHotspot(String(h.id))}
                  disabled={props.mode === 'edit'}
                  data-testid={`splits-hotspot-checkbox-${h.id}`}
                />
                {h.name}
              </label>
            ))}
          </div>
        </div>

        {/* Typ */}
        <div>
          <span className="label">{t('editor.type')}</span>
          <div className="segment" role="group" aria-label={t('editor.type')} style={{ width: '100%' }}>
            <button
              type="button"
              className={form.split_type === 'percentage' ? 'is-active' : undefined}
              style={{ flex: 1 }}
              onClick={() => set('split_type', 'percentage')}
              data-testid="splits-type-percentage"
            >
              {t('editor.typePercent')}
            </button>
            <button
              type="button"
              className={form.split_type === 'fixed_amount' ? 'is-active' : undefined}
              style={{ flex: 1 }}
              onClick={() => set('split_type', 'fixed_amount')}
              data-testid="splits-type-fixed"
            >
              {t('editor.typeFixed')}
            </button>
          </div>
        </div>

        {/* Basis */}
        <div>
          <span className="label">{t('editor.basis')}</span>
          <div className="segment" role="group" aria-label={t('editor.basis')} style={{ width: '100%' }}>
            <button
              type="button"
              className={form.basis === 'hnt_rewards' ? 'is-active' : undefined}
              style={{ flex: 1 }}
              onClick={() => set('basis', 'hnt_rewards')}
              data-testid="splits-basis-hnt"
            >
              {t('editor.basisHnt')}
            </button>
            <button
              type="button"
              className={form.basis === 'gigabytes_transferred' ? 'is-active' : undefined}
              style={{ flex: 1 }}
              onClick={() => set('basis', 'gigabytes_transferred')}
              data-testid="splits-basis-gb"
            >
              {t('editor.basisGb')}
            </button>
          </div>
        </div>

        {/* Auszahlungswaehrung */}
        <div>
          <span className="label" id="splits-currency-label">{t('editor.payoutCurrency')}</span>
          <select
            className="select"
            aria-labelledby="splits-currency-label"
            value={form.payout_currency}
            onChange={(e) => set('payout_currency', e.target.value as Currency)}
            data-testid="splits-currency-select"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Wert */}
        <div>
          <label className="label" htmlFor="splits-value">
            {valueLabel}
          </label>
          <input
            id="splits-value"
            className="input mono tnum"
            type="number"
            min="0"
            max={form.split_type === 'percentage' ? '100' : undefined}
            step={form.split_type === 'percentage' ? '0.5' : '0.01'}
            value={form.value}
            onChange={(e) => set('value', e.target.value)}
            aria-invalid={valueError ? true : undefined}
            aria-describedby={valueError ? 'splits-value-error' : undefined}
            data-testid="splits-value-input"
          />
          {valueError && (
            <p
              className="alert-error"
              id="splits-value-error"
              role="alert"
              data-testid="splits-value-error"
            >
              {valueError}
            </p>
          )}
        </div>

        {/* Gueltigkeit */}
        {/*
          Bewusste Entscheidung (Byzantine-Nachbesserung): Der native
          <input type="date"> zeigt den Platzhalter (z.B. mm/dd/yyyy vs.
          tt.mm.jjjj) immer in der Browser-/OS-Sprache des Nutzers, nicht in
          der App-Sprache (i18n) — das `lang`-Attribut auf dem Element hat
          darauf keinen Einfluss, Chrome/Firefox/Safari leiten das Format
          ausschliesslich aus der Browser-Locale ab. Ein Custom-Picker wuerde
          Tastatur-/Screenreader-Bedienung des nativen Inputs verlieren, daher
          bewusst kein Umbau — das gerenderte Datum selbst folgt weiterhin dem
          ISO-Wert (value/onChange), nur der Platzhalter ist browsergesteuert.
        */}
        <div>
          <label className="label" htmlFor="splits-start-date">
            {t('editor.startDate')}
          </label>
          <input
            id="splits-start-date"
            className="input mono"
            type="date"
            value={form.start_date}
            onChange={(e) => set('start_date', e.target.value)}
            data-testid="splits-start-date"
          />
        </div>
        <div>
          <label className="label" htmlFor="splits-end-date">
            {t('editor.endDate')}
          </label>
          <input
            id="splits-end-date"
            className="input mono"
            type="date"
            min={form.start_date || undefined}
            value={form.end_date}
            onChange={(e) => set('end_date', e.target.value)}
            aria-invalid={endDateError ? true : undefined}
            aria-describedby={endDateError ? 'splits-end-date-error' : undefined}
            data-testid="splits-end-date"
          />
          {endDateError && (
            <p
              className="alert-error"
              id="splits-end-date-error"
              role="alert"
              data-testid="splits-end-date-error"
            >
              {endDateError}
            </p>
          )}
        </div>

        {/* Notizen */}
        <div>
          <label className="label" htmlFor="splits-notes">
            {t('editor.notes')}
          </label>
          <textarea
            id="splits-notes"
            className="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            data-testid="splits-notes"
          />
        </div>

        {/* Vorschau-Annahmen */}
        <div>
          <span className="label">{t('preview.assumptionsLabel')}</span>
          <div className="splits-assumptions">
            {form.basis === 'gigabytes_transferred' ? (
              <>
                <div>
                  <label className="label" htmlFor="splits-assume-gb">
                    {t('preview.gigabytes')}
                  </label>
                  <input
                    id="splits-assume-gb"
                    className="input mono tnum"
                    type="number"
                    min="0"
                    value={assumptions.gigabytes}
                    onChange={(e) => setAssumption('gigabytes', e.target.value)}
                    data-testid="splits-assume-gb"
                  />
                </div>
                <div>
                  <label className="label" htmlFor="splits-assume-rate">
                    {t('preview.ratePerGb')}
                  </label>
                  <input
                    id="splits-assume-rate"
                    className="input mono tnum"
                    type="number"
                    min="0"
                    step="0.005"
                    value={assumptions.rate_per_gb}
                    onChange={(e) => setAssumption('rate_per_gb', e.target.value)}
                    data-testid="splits-assume-rate"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="label" htmlFor="splits-assume-hnt">
                  {t('preview.hntRewards')}
                </label>
                <input
                  id="splits-assume-hnt"
                  className="input mono tnum"
                  type="number"
                  min="0"
                  value={assumptions.hnt_rewards}
                  onChange={(e) => setAssumption('hnt_rewards', e.target.value)}
                  data-testid="splits-assume-hnt"
                />
              </div>
            )}
            {needsHntUsd && (
              <div>
                <label className="label" htmlFor="splits-assume-hnt-usd">
                  {t('preview.hntUsd')}
                </label>
                <input
                  id="splits-assume-hnt-usd"
                  className="input mono tnum"
                  type="number"
                  min="0"
                  step="0.01"
                  value={assumptions.hnt_usd}
                  onChange={(e) => setAssumption('hnt_usd', e.target.value)}
                  data-testid="splits-assume-hnt-usd"
                />
              </div>
            )}
            {needsEurUsd && (
              <div>
                <label className="label" htmlFor="splits-assume-eur-usd">
                  {t('preview.eurUsd')}
                </label>
                <input
                  id="splits-assume-eur-usd"
                  className="input mono tnum"
                  type="number"
                  min="0"
                  step="0.01"
                  value={assumptions.eur_usd}
                  onChange={(e) => setAssumption('eur_usd', e.target.value)}
                  data-testid="splits-assume-eur-usd"
                />
              </div>
            )}
          </div>
        </div>

        {/* Live-Vorschau */}
        <SplitPreviewLine request={previewRequest} preview={preview} hostName={hostName} />

        {/* Speicherfehler (422 sum-overlap etc. — Meldung lokalisiert vom BE) */}
        {saveError && (
          <p className="alert-error" role="alert" data-testid="splits-save-error">
            {saveError}
          </p>
        )}

        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1 }}
            disabled={!canSave || saveMutation.isPending}
            onClick={() => void handleSave()}
            data-testid="splits-save"
          >
            {saveMutation.isPending ? t('editor.saving') : t('editor.save')}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void navigate({ to: '/splits' })}
            data-testid="splits-discard"
          >
            {t('editor.discard')}
          </button>
        </div>
        <p className="muted" style={{ fontSize: 11, margin: 0 }}>
          {t('editor.footnote')}
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Vorschau-Zeile (Vorlage .preview-line, aria-live)
// ---------------------------------------------------------------------------

type SplitPreviewLineProps = {
  request: SplitPreviewRequest | null
  preview: ReturnType<typeof useSplitPreview>
  hostName: string
}

/**
 * Rendert den Vorschau-Satz AUSSCHLIESSLICH aus dem Snapshot der Antwort
 * (Annahmen + Ergebnis desselben Requests) — nie aus den aktuellen
 * Formularwerten. Solange die angezeigte Antwort nicht zur aktuellen Eingabe
 * gehoert (Debounce/Fetch/Platzhalter), erscheint die Zeile gedimmt mit
 * „wird berechnet …“ und aria-busy, damit aria-live keinen falschen
 * Kombi-Satz vorliest.
 */
function SplitPreviewLine({ request, preview, hostName }: SplitPreviewLineProps) {
  const { t } = useTranslation('splits')

  const snapshot = preview.data
  const isRecalculating = preview.isRecalculating

  let content: string
  let isError = false

  if (request == null) {
    content = t('preview.idle')
  } else if (preview.isError && !isRecalculating) {
    isError = true
    const message = apiErrorMessage(preview.error)
    content = message ? t('preview.error', { message }) : t('preview.errorGeneric')
  } else if (snapshot) {
    const { request: assumed, result } = snapshot
    const share = formatNumber(result.host_share ?? 0, result.host_share_currency === 'HNT' ? 4 : 2)
    content =
      assumed.basis === 'gigabytes_transferred'
        ? t('preview.resultGb', {
            gb: formatNumber(assumed.gigabytes ?? 0, 0),
            rate: formatNumber(assumed.rate_per_gb ?? 0, 2),
            host: hostName,
            share,
            currency: result.host_share_currency ?? '',
          })
        : t('preview.resultHnt', {
            hnt: formatNumber(assumed.hnt_rewards ?? 0, 0),
            host: hostName,
            share,
            currency: result.host_share_currency ?? '',
          })
  } else {
    content = t('preview.pending')
  }

  // Der veraltete Satz bleibt lesbar (kein Flackern), ist aber sichtbar als
  // „noch nicht aktuell“ markiert.
  const showStale = isRecalculating && snapshot != null

  return (
    <div
      className={`splits-preview-line${isError ? ' is-error' : ''}${
        isRecalculating ? ' is-recalculating' : ''
      }`}
      aria-live="polite"
      aria-busy={isRecalculating || undefined}
      data-recalculating={isRecalculating || undefined}
      data-testid="splits-preview-line"
    >
      <span>{content}</span>
      {showStale && (
        <span className="splits-preview-pending" data-testid="splits-preview-pending">
          {t('preview.recalculating')}
        </span>
      )}
    </div>
  )
}
